import { useCurrencyConversion } from '@/hooks/useCurrencyConversion';
import type { ParsedDocument } from '@/types/parsedDocument';

interface UseParsedDocumentConversionOptions {
  /** The AI-extracted document data (currency + total + dates) */
  parsedData: ParsedDocument | null | undefined;
  /** Currency the form is denominated in (org base) */
  targetCurrency: string | null | undefined;
  /** Gate for the whole feature (e.g. false when editing an existing record) */
  enabled: boolean;
}

export type ParsedDocumentConversionStatus =
  | 'idle'        // no conversion needed (same/unknown currency, or disabled)
  | 'pending'     // rate lookup in flight — hold the prefill
  | 'converted'   // rate available — prefill converted amounts
  | 'unavailable'; // rate lookup failed — prefill raw amounts + warn

export interface ParsedDocumentConversion {
  status: ParsedDocumentConversionStatus;
  /** exchange rate docCurrency → targetCurrency (only when status = 'converted') */
  rate?: number;
  /** date the rate applies to, YYYY-MM-DD (only when status = 'converted') */
  rateDate?: string;
  /** normalized currency the document is denominated in, when detected */
  docCurrency?: string;
}

function normalizeCurrency(currency: string | null | undefined): string | undefined {
  const normalized = currency?.trim().toUpperCase();
  return normalized || undefined;
}

/**
 * Resolve the document's transaction/invoice date as a UTC-midnight Date for
 * historical rate lookup. useCurrencyConversion serializes via
 * date.toISOString().slice(0, 10), so a local-midnight Date would shift to the
 * previous day in UTC+ timezones — hence the explicit 'Z' construction from
 * the ISO date prefix. Non-ISO date strings fall back to undefined (today's rate).
 */
function getDocumentRateDate(doc: ParsedDocument | null | undefined): Date | undefined {
  const dateStr = doc?.invoiceDate || doc?.transactionDate;
  if (!dateStr) return undefined;

  const isoPrefix = /^(\d{4}-\d{2}-\d{2})/.exec(dateStr.trim());
  if (!isoPrefix) return undefined;

  const date = new Date(`${isoPrefix[1]}T00:00:00Z`);
  return isNaN(date.getTime()) ? undefined : date;
}

/**
 * Exchange-rate lookup for prefilling a form from a scanned document whose
 * currency differs from the organization's base currency (e.g. a EUR receipt
 * for a purchase settled on a USD credit card).
 *
 * The rate is the ECB mid-market rate for the document date — an ESTIMATE of
 * what the card issuer actually charged (their rate includes a spread), good
 * enough to prefill and to match against the bank/credit-card feed later.
 */
export function useParsedDocumentConversion({
  parsedData,
  targetCurrency,
  enabled,
}: UseParsedDocumentConversionOptions): ParsedDocumentConversion {
  const docCurrency = normalizeCurrency(parsedData?.currency);
  const target = normalizeCurrency(targetCurrency);
  const needsConversion =
    enabled && !!docCurrency && !!target && docCurrency !== target;

  const conversion = useCurrencyConversion({
    amount: parsedData?.total ?? 1, // the rate is what matters; total gives a sane cached key
    fromCurrency: docCurrency ?? '',
    toCurrency: target ?? '',
    date: getDocumentRateDate(parsedData),
    enabled: needsConversion,
    // Fail fast: get_exchange_rate raises when no rate exists, and the default
    // 3-retry backoff would hold the form prefill for many seconds.
    retry: false,
  });

  if (!needsConversion) {
    return { status: 'idle', docCurrency };
  }
  if (conversion.isError) {
    return { status: 'unavailable', docCurrency };
  }
  if (conversion.data) {
    return {
      status: 'converted',
      rate: conversion.data.exchangeRate,
      rateDate: conversion.data.rateDate,
      docCurrency,
    };
  }
  return { status: 'pending', docCurrency };
}
